import { useEffect, useState } from "react";

import { PlusSmallIcon } from "@heroicons/react/24/outline";

import TriageStepModal from "../../components/triage/TriageStepModal";
import TriageStepsTable from "../../components/triage/TriageStepsTable";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useTriage from "../../data/useTriage";
import { ITriageStep } from "../../models/Triage";

export default function TriageEditor() {
  const { setInfo } = usePageNameContext();
  const { allTriageSteps, updateTriageStepOrder } = useTriage(undefined, undefined, true);
  const [triageSteps, setTriageSteps] = useState<ITriageStep[] | undefined>(undefined);
  const [editTriageStep, setEditTriageStep] = useState<ITriageStep | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Triage Editor",
      desc: "View and manage vehicle triage steps",
    });
  }, [setInfo]);

  useEffect(() => {
    if (!allTriageSteps.isLoading && allTriageSteps.data) {
      const orderedSteps = [...allTriageSteps.data.data].sort((a, b) => a.order - b.order);
      setTriageSteps(orderedSteps);
      setIsLoading(false);
    }
  }, [allTriageSteps.isLoading, allTriageSteps.data]);

  const handleSetTriageStep = (triageStep: ITriageStep) => {
    setEditTriageStep(triageStep);
    setCreateOpen(true);
  };

  const handleAddTriageStep = () => {
    setEditTriageStep(undefined);
    setCreateOpen(true);
  };

  const handleMoveTriageStep = async (stepId: string, direction: "up" | "down") => {
    if (!triageSteps || isReordering) return;

    const orderedSteps = [...triageSteps].sort((a, b) => a.order - b.order);
    const index = orderedSteps.findIndex((x) => x.id === stepId);

    if (index < 0) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= orderedSteps.length) return;

    setIsReordering(true);

    try {
      await new Promise<void>((resolve, reject) => {
        updateTriageStepOrder.mutate(
          { triageStepId: stepId, newIndex: targetIndex },
          {
            onSuccess: () => resolve(),
            onError: reject,
          }
        );
      });

      await allTriageSteps.refetch();
    } catch {
      await allTriageSteps.refetch();
    } finally {
      setIsReordering(false);
    }
  };

  const nextOrder = (triageSteps?.length ?? 0) + 1;

  return allTriageSteps.isLoading || isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <TriageStepModal
        open={createOpen}
        setOpen={setCreateOpen}
        triageStep={editTriageStep}
        defaultOrder={nextOrder}
      />
      <div className="mt-4 flex justify-between">
        <button
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={handleAddTriageStep}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Add New Triage Step
        </button>
      </div>

      <TriageStepsTable
        triageSteps={triageSteps!}
        setTriageStep={handleSetTriageStep}
        moveTriageStep={handleMoveTriageStep}
        isReordering={isReordering}
      />
    </>
  );
}
